import { createContext, useContext, useEffect, useState, useRef } from "react";

const TimerContext = createContext();

export const TimerProvider = ({ children }) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const [exerciseTimeLeft, setExerciseTimeLeft] = useState(0); 
  const [sleepTimeLeft, setSleepTimeLeft] = useState(0); 
  const [mindfulnessTimeLeft, setMindfulnessTimeLeft] = useState(0); 
  const [quizTimeLeft, setQuizTimeLeft] = useState(0); // New quiz timer
  
  const intervalRef = useRef(null);
  const exerciseIntervalRef = useRef(null);
  const sleepIntervalRef = useRef(null);
  const mindfulnessIntervalRef = useRef(null);
  const quizIntervalRef = useRef(null); // New quiz interval ref
  
  const [hasExtended, setHasExtended] = useState(false);

  // Helper: Get time from localStorage and calculate time left for Global Timer
  const restoreTimer = () => {
    const storedStart = localStorage.getItem("timerStart");
    const storedDuration = localStorage.getItem("timerDuration");
    const storedHasExtended = localStorage.getItem("timerHasExtended") === "true";

    if (storedStart && storedDuration) {
      const start = parseInt(storedStart, 10);
      const duration = parseInt(storedDuration, 10);
      const now = Math.floor(Date.now() / 1000);
      const elapsed = now - start;
      const remaining = Math.max(duration - elapsed, 0);
      setTimeLeft(remaining);
      setHasExtended(storedHasExtended);
      if (remaining > 0) startCountdown();
    }
  };

  // Restore exercise timer
  const restoreExerciseTimer = () => {
    const storedExerciseStart = localStorage.getItem("exerciseTimerStart");
    const storedExerciseDuration = localStorage.getItem("exerciseTimerDuration");

    if (storedExerciseStart && storedExerciseDuration) {
      const start = parseInt(storedExerciseStart, 10);
      const duration = parseInt(storedExerciseDuration, 10);
      const now = Math.floor(Date.now() / 1000);
      const elapsed = now - start;
      const remaining = Math.max(duration - elapsed, 0);
      setExerciseTimeLeft(remaining);
      if (remaining > 0) startExerciseCountdown();
    }
  };

  // Restore sleep timer
  const restoreSleepTimer = () => {
    const storedSleepStart = localStorage.getItem("sleepTimerStart");
    const storedSleepDuration = localStorage.getItem("sleepTimerDuration");

    if (storedSleepStart && storedSleepDuration) {
      const start = parseInt(storedSleepStart, 10);
      const duration = parseInt(storedSleepDuration, 10);
      const now = Math.floor(Date.now() / 1000);
      const elapsed = now - start;
      const remaining = Math.max(duration - elapsed, 0);
      setSleepTimeLeft(remaining);
      if (remaining > 0) startSleepCountdown();
    }
  };

  // Restore mindfulness timer
  const restoreMindfulnessTimer = () => {
    const storedMindfulnessStart = localStorage.getItem("mindfulnessTimerStart");
    const storedMindfulnessDuration = localStorage.getItem("mindfulnessTimerDuration");

    if (storedMindfulnessStart && storedMindfulnessDuration) {
      const start = parseInt(storedMindfulnessStart, 10);
      const duration = parseInt(storedMindfulnessDuration, 10);
      const now = Math.floor(Date.now() / 1000);
      const elapsed = now - start;
      const remaining = Math.max(duration - elapsed, 0);
      setMindfulnessTimeLeft(remaining);
      if (remaining > 0) startMindfulnessCountdown();
    }
  };

  // NEW: Restore quiz timer
  const restoreQuizTimer = () => {
    const storedQuizStart = localStorage.getItem("quizTimerStart");
    const storedQuizDuration = localStorage.getItem("quizTimerDuration");

    if (storedQuizStart && storedQuizDuration) {
      const start = parseInt(storedQuizStart, 10);
      const duration = parseInt(storedQuizDuration, 10);
      const now = Math.floor(Date.now() / 1000);
      const elapsed = now - start;
      const remaining = Math.max(duration - elapsed, 0);
      setQuizTimeLeft(remaining);
      if (remaining > 0) startQuizCountdown();
    }
  };

  // Global timer countdown
  const startCountdown = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        const newTime = prev - 1;
        
        if (newTime <= 0) {
          clearInterval(intervalRef.current);
          localStorage.removeItem("timerStart");
          localStorage.removeItem("timerDuration");
          localStorage.removeItem("timerHasExtended");
          return 0;
        }
        return newTime;
      });
    }, 1000);
  };

  // Exercise timer countdown
  const startExerciseCountdown = () => {
    if (exerciseIntervalRef.current) clearInterval(exerciseIntervalRef.current);

    exerciseIntervalRef.current = setInterval(() => {
      setExerciseTimeLeft((prev) => {
        const newTime = prev - 1;
        
        if (newTime <= 0) {
          clearInterval(exerciseIntervalRef.current);
          localStorage.removeItem("exerciseTimerStart");
          localStorage.removeItem("exerciseTimerDuration");
          return 0;
        }
        return newTime;
      });
    }, 1000);
  };

  // Sleep timer countdown
  const startSleepCountdown = () => {
    if (sleepIntervalRef.current) clearInterval(sleepIntervalRef.current);

    sleepIntervalRef.current = setInterval(() => {
      setSleepTimeLeft((prev) => {
        const newTime = prev - 1;
        
        if (newTime <= 0) {
          clearInterval(sleepIntervalRef.current);
          localStorage.removeItem("sleepTimerStart");
          localStorage.removeItem("sleepTimerDuration");
          return 0;
        }
        return newTime;
      });
    }, 1000);
  };

  // Mindfulness timer countdown
  const startMindfulnessCountdown = () => {
    if (mindfulnessIntervalRef.current) clearInterval(mindfulnessIntervalRef.current);

    mindfulnessIntervalRef.current = setInterval(() => {
      setMindfulnessTimeLeft((prev) => {
        const newTime = prev - 1;
        
        if (newTime <= 0) {
          clearInterval(mindfulnessIntervalRef.current);
          localStorage.removeItem("mindfulnessTimerStart");
          localStorage.removeItem("mindfulnessTimerDuration");
          return 0;
        }
        return newTime;
      });
    }, 1000);
  };

  // NEW: Quiz timer countdown
  const startQuizCountdown = () => {
    if (quizIntervalRef.current) clearInterval(quizIntervalRef.current);

    quizIntervalRef.current = setInterval(() => {
      setQuizTimeLeft((prev) => {
        const newTime = prev - 1;
        
        if (newTime <= 0) {
          clearInterval(quizIntervalRef.current);
          localStorage.removeItem("quizTimerStart");
          localStorage.removeItem("quizTimerDuration");
          return 0;
        }
        return newTime;
      });
    }, 1000);
  };

  // Start global timer
  const startGlobalTimer = (initialSeconds) => {
    const start = Math.floor(Date.now() / 1000);
    localStorage.setItem("timerStart", start.toString());
    localStorage.setItem("timerDuration", initialSeconds.toString());
    localStorage.setItem("timerHasExtended", "false");
    setTimeLeft(initialSeconds);
    setHasExtended(false);
    startCountdown();
  };

  // Start exercise timer
  const startExerciseTimer = (initialSeconds) => {
    const start = Math.floor(Date.now() / 1000);
    localStorage.setItem("exerciseTimerStart", start.toString());
    localStorage.setItem("exerciseTimerDuration", initialSeconds.toString());
    setExerciseTimeLeft(initialSeconds);
    startExerciseCountdown();
  };

  // Start sleep timer
  const startSleepTimer = (initialSeconds) => {
    const start = Math.floor(Date.now() / 1000);
    localStorage.setItem("sleepTimerStart", start.toString());
    localStorage.setItem("sleepTimerDuration", initialSeconds.toString());
    setSleepTimeLeft(initialSeconds);
    startSleepCountdown();
  };

  // Start mindfulness timer
  const startMindfulnessTimer = (initialSeconds) => {
    const start = Math.floor(Date.now() / 1000);
    localStorage.setItem("mindfulnessTimerStart", start.toString());
    localStorage.setItem("mindfulnessTimerDuration", initialSeconds.toString());
    setMindfulnessTimeLeft(initialSeconds);
    startMindfulnessCountdown();
  };

  // NEW: Start quiz timer
  const startQuizTimer = (initialSeconds) => {
    const start = Math.floor(Date.now() / 1000);
    localStorage.setItem("quizTimerStart", start.toString());
    localStorage.setItem("quizTimerDuration", initialSeconds.toString());
    setQuizTimeLeft(initialSeconds);
    startQuizCountdown();
  };

  // Start all timers at once (updated with quiz timer)
  const startAllGlobalTimer = (
    globalSeconds = 540, 
    exerciseSeconds = 240, 
    sleepSeconds = 480, 
    mindfulnessSeconds = 300,
    quizSeconds = 300 // Default quiz timer (5 minutes)
  ) => {
    startGlobalTimer(globalSeconds);
    startExerciseTimer(exerciseSeconds);
    startSleepTimer(sleepSeconds);
    startMindfulnessTimer(mindfulnessSeconds);
    startQuizTimer(quizSeconds);
  };

  // Stop individual timers
  const stopGlobalTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    localStorage.removeItem("timerStart");
    localStorage.removeItem("timerDuration");
    localStorage.removeItem("timerHasExtended");
    setTimeLeft(0);
  };

  const stopExerciseTimer = () => {
    if (exerciseIntervalRef.current) {
      clearInterval(exerciseIntervalRef.current);
      exerciseIntervalRef.current = null;
    }
    localStorage.removeItem("exerciseTimerStart");
    localStorage.removeItem("exerciseTimerDuration");
    setExerciseTimeLeft(0);
  };

  const stopSleepTimer = () => {
    if (sleepIntervalRef.current) {
      clearInterval(sleepIntervalRef.current);
      sleepIntervalRef.current = null;
    }
    localStorage.removeItem("sleepTimerStart");
    localStorage.removeItem("sleepTimerDuration");
    setSleepTimeLeft(0);
  };

  const stopMindfulnessTimer = () => {
    if (mindfulnessIntervalRef.current) {
      clearInterval(mindfulnessIntervalRef.current);
      mindfulnessIntervalRef.current = null;
    }
    localStorage.removeItem("mindfulnessTimerStart");
    localStorage.removeItem("mindfulnessTimerDuration");
    setMindfulnessTimeLeft(0);
  };

  // NEW: Stop quiz timer
  const stopQuizTimer = () => {
    if (quizIntervalRef.current) {
      clearInterval(quizIntervalRef.current);
      quizIntervalRef.current = null;
    }
    localStorage.removeItem("quizTimerStart");
    localStorage.removeItem("quizTimerDuration");
    setQuizTimeLeft(0);
  };

  // Restore all timers on mount (updated with quiz timer)
  useEffect(() => {
    restoreTimer();
    restoreExerciseTimer();
    restoreSleepTimer();
    restoreMindfulnessTimer();
    restoreQuizTimer(); // Restore quiz timer
    
    return () => {
      clearInterval(intervalRef.current);
      clearInterval(exerciseIntervalRef.current);
      clearInterval(sleepIntervalRef.current);
      clearInterval(mindfulnessIntervalRef.current);
      clearInterval(quizIntervalRef.current); // Clean up quiz interval
    };
  }, []);

  // Persist current timeLeft to localStorage for display purposes
  useEffect(() => {
    localStorage.setItem("timerLeft", timeLeft.toString());
  }, [timeLeft]);

  useEffect(() => {
    localStorage.setItem("exerciseTimeLeft", exerciseTimeLeft.toString());
  }, [exerciseTimeLeft]);

  useEffect(() => {
    localStorage.setItem("sleepTimeLeft", sleepTimeLeft.toString());
  }, [sleepTimeLeft]);

  useEffect(() => {
    localStorage.setItem("mindfulnessTimeLeft", mindfulnessTimeLeft.toString());
  }, [mindfulnessTimeLeft]);

  // NEW: Persist quiz time left
  useEffect(() => {
    localStorage.setItem("quizTimeLeft", quizTimeLeft.toString());
  }, [quizTimeLeft]);

  return (
    <TimerContext.Provider value={{ 
      // Timer states
      timeLeft, 
      exerciseTimeLeft,
      sleepTimeLeft,
      mindfulnessTimeLeft,
      quizTimeLeft, // New quiz timer state
      hasExtended,
      
      // Start individual timers
      startGlobalTimer,
      startExerciseTimer,
      startSleepTimer,
      startMindfulnessTimer,
      startQuizTimer, // New start quiz timer function
      
      // Start all timers
      startAllGlobalTimer,
      
      // Stop individual timers
      stopGlobalTimer,
      stopExerciseTimer,
      stopSleepTimer,
      stopMindfulnessTimer,
      stopQuizTimer, // New stop quiz timer function
      
      // Setters (if needed for manual control)
      setTimeLeft,
      setExerciseTimeLeft,
      setSleepTimeLeft,
      setMindfulnessTimeLeft,
      setQuizTimeLeft // New quiz timer setter
    }}>
      {children}
    </TimerContext.Provider>
  );
};

export const useTimer = () => useContext(TimerContext);